import React, { lazy } from 'react';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Eye, EyeSlash } from '@phosphor-icons/react';
const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));
import { Link as RouterLink } from 'react-router';

import { loginSchema } from '../../schemas/index';
import { recaptchaSiteKey } from '../../constants/index';
import ForgotPasswordModal from './ForgotPassword';
import { useAuth } from '../../hooks/useAuth';

import { ILoginCredentials } from '../../schemas/apis/interface';
import { routes } from '../../constants/routes';
import theme from '../../theme';
import CommonAuthLayout from './CommonAuthLayout';

const SigninPage: React.FC = () => {
  const { login, setAlert } = useAuth();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);

  // const handleGoogleLogin = (tokenResponse: { access_token: string }) => {
  //   void (async () => {
  //     try {
  //       const response = await axios.get<GoogleUserInfo>(
  //         'https://www.googleapis.com/oauth2/v3/userinfo',
  //         {
  //           headers: {
  //             Authorization: `Bearer ${tokenResponse.access_token}`,
  //           },
  //         }
  //       );
  //       // @ts-expect-error
  //       const { given_name, family_name, name, sub, picture, email } =
  //         response.data;

  //       await googleLogin({
  //         name:
  //           name ||
  //           (given_name && family_name ? `${given_name} ${family_name}` : ''),
  //         email: email || '',
  //         googleId: sub || '',
  //         googleImage: picture || '',
  //       });
  //     } catch (error) {
  //       console.error('Error fetching Google user info', error);
  //       setAlert({
  //         open: true,
  //         message: 'Error fetching Google user info',
  //         severity: 'error',
  //       });
  //     }
  //   })();
  // };

  // const loginWithGoogle = useGoogleLogin({
  //   onSuccess: handleGoogleLogin,
  //   onError: () => {
  //     setAlert({
  //       open: true,
  //       message: 'Google login failed',
  //       severity: 'error',
  //     });
  //   },
  // });

  const formik = useFormik<ILoginCredentials>({
    initialValues: {
      email: '',
      password: '',
      recaptchaToken: null,
    },
    validationSchema: loginSchema,
    onSubmit: async values => {
      try {
        await login(values);
      } catch (error) {
        console.error('Login failed', error);
        setAlert({
          open: true,
          // @ts-expect-error
          message: error?.response?.data?.message || 'Login failed',
          severity: 'error',
        });
      }
    },
  });

  const handleRecaptchaChange = (token: string | null) => {
    void formik.setFieldValue('recaptchaToken', token);
  };

  return (
    <CommonAuthLayout>
      <Box
        component={Paper}
        elevation={0}
        sx={{
          flex: '1 1 50%',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2, sm: 3, md: 4, lg: 5 },
          bgcolor: 'white',
          overflow: 'auto',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              px: '24px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: '#2B2D3F',
                fontSize: '24px',
              }}
            >
              Log in to your account
            </Typography>
            <Typography
              sx={{
                color: '#6B6F80',
                fontSize: '14px',
              }}
            >
              Access your dashboard & customize assistants effortlessly
            </Typography>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ mb: 2, px: '24px' }}>
              <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
                Email ID
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                placeholder="Email ID"
                size="small"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>

            <Box sx={{ mb: 2, px: '24px' }}>
              <Typography variant="body2" sx={{ mb: 0.5, color: '#6B6F80' }}>
                Password
              </Typography>
              <TextField
                fullWidth
                id="password"
                name="password"
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                size="small"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      {showPassword ? (
                        <EyeSlash size={16} color="gray" />
                      ) : (
                        <Eye size={16} color="gray" />
                      )}
                    </Button>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                px: '24px',
                mb: 2,
              }}
            >
              <Box
                sx={{
                  transform: { xs: 'scale(0.9)', sm: 'scale(0.85)' },
                  transformOrigin: 'left top',
                }}
              >
                <ReCAPTCHA
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaChange}
                />
              </Box>
              <Link
                component="button"
                type="button"
                onClick={() => setShowForgotPassword(true)}
                sx={{
                  color: '#6B6F80',
                  fontSize: '14px',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={!formik.values.recaptchaToken}
              sx={{
                py: 1.5,
                mx: '24px',
                bgcolor: theme.palette.primary.main,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                mb: 2,
                fontSize: '14px',
                height: '34px',
                width: 'calc(100% - 48px)',
              }}
            >
              Login
            </Button>

            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                color: '#6B6F80',
              }}
            >
              Don't have an account?{' '}
              <Link
                component={RouterLink}
                to={routes.signup}
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Sign Up
              </Link>
            </Typography>
          </form>
        </Container>

        <ForgotPasswordModal
          open={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          onSuccess={message =>
            setAlert({ open: true, message, severity: 'success' })
          }
          onError={message =>
            setAlert({ open: true, message, severity: 'error' })
          }
        />
      </Box>
    </CommonAuthLayout>
  );
};

export default SigninPage;
